/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, X as XIcon, ChevronDown, Loader2, Quote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RED = "#E32126";
// One-time event: Saturday 19 September 2026 · 11:00 AM IST
// Encoded as UTC (11:00 IST = 05:30 UTC)
const EVENT_START_UTC = Date.UTC(2026, 8, 19, 5, 30, 0); // month is 0-indexed → 8 = September
const EVENT_LABEL_LONG = "Saturday, 19 September · 11:00 AM – 2:00 PM IST";
const EVENT_LABEL_SHORT = "Sat, 19 Sep · 11 AM–2 PM";

// ============================================================
// Copy — DO NOT modify without explicit user approval
// ============================================================
const PROBLEMS = [
  { t: "Niche unclear", d: "You're speaking to everyone, so no one feels spoken to." },
  { t: "Message confusing", d: "Your offer takes three sentences to explain." },
  { t: "Funnel leaking", d: "Leads land with nowhere clear to go." },
  { t: "No nurture", d: "Follow-up never happens on time, so warm leads go cold." },
];

const OUTCOMES = [
  {
    t: "Get found by the right people, not everyone.",
    d: "Learn how to identify and speak directly to your highest-value clients — so your content and ads finally attract buyers, not browsers.",
  },
  {
    t: "Stop losing leads to a broken funnel.",
    d: "Learn how to set up a simple landing page, booking system and follow-up flow that nurtures every lead automatically, even while you sleep.",
  },
  {
    t: "Convert calls into clients, consistently.",
    d: "Learn the 8 Pillars of Sales used to close consultation calls without sounding pushy or discounting your price.",
  },
  {
    t: "Build a calendar that fills itself.",
    d: "Learn how to combine ads, content and automation into one connected system — so new bookings show up on your calendar without daily manual outreach.",
  },
  {
    t: "Scale without burning out.",
    d: "Learn how to run this system with your existing time and team, so growth doesn't mean working more hours.",
  },
];

const WHO_FOR = [
  { yes: true, t: "You're a coach, consultant or agency owner who wants consistent, predictable clients." },
  { yes: true, t: "You're getting leads but struggling to convert them into paying clients." },
  { yes: true, t: "You want one simple system instead of five disconnected tools and tactics." },
  { yes: false, t: "You're looking for a done-for-you service with zero involvement from you." },
];

const TESTIMONIALS = [
  {
    q: "Before this, I was posting content with no clear system behind it. Within a few months of applying this framework, I went from 0 to 47K YouTube subscribers and started getting inbound client enquiries every week.",
    who: "Career & Tech Coach",
  },
  {
    q: "My leads were fine, but conversion was the problem. Once I fixed my funnel and follow-up the way it's taught here, my consultation calls started closing at almost double the rate.",
    who: "Fitness & Wellness Coach",
  },
  {
    q: "I used to run ads with no clarity on who I was even targeting. This system helped me lock my ideal client first — my cost per lead dropped and lead quality went up immediately.",
    who: "Business Consultant",
  },
  {
    q: "We had to actually set up an IVR because the number of calls coming in got overwhelming. That one shift in our lead system changed everything.",
    who: "Agency Owner",
  },
];

const CLIENTS = [
  { name: "HCL Tech",        src: null },
  { name: "IBM",             src: "/assets/clients/ibm.png" },
  { name: "Wipro",           src: "/assets/clients/wipro.png" },
  { name: "Knowledgewoods",  src: "/assets/clients/knowledgewoods.png" },
  { name: "EIFS",            src: null },
  { name: "Booming Bulls",   src: "/assets/clients/booming-bulls.png" },
  { name: "Response Fabrics",src: "/assets/clients/response-fabrics.png" },
  { name: "DSIM",            src: "/assets/clients/dsim.png" },
  { name: "Aman Travels",    src: "/assets/clients/aman-travels.png" },
  { name: "British Telecom", src: null },
  { name: "Cemex UK",        src: "/assets/clients/cemex.png" },
  { name: "AT&T US",         src: "/assets/clients/att.png" },
];

const FAQS = [
  {
    q: "Is this webinar just a sales pitch?",
    a: "No. You'll walk away with a real, usable plan for your niche, funnel and sales calls — whether or not you ever work with us again.",
  },
  {
    q: "I'm not techy — can I still apply what's taught?",
    a: "Yes. Every tech step (landing page, booking system, CRM, WhatsApp) is explained simply, with no coding required.",
  },
  {
    q: "Is registration really free?",
    a: "Yes. Registration is 100% free — you'll get a confirmation email, the live join link and a WhatsApp community invite. If you can't attend, no penalty; the recording availability will be shared over email.",
  },
  {
    q: "Will I get a recording?",
    a: "This webinar runs live to keep the Q&A relevant to who's in the room. Recording availability will be shared in your confirmation email.",
  },
  {
    q: "Does this work for B2B or technical niches?",
    a: "Yes — this system performs especially well in fintech, education, SaaS and coaching businesses.",
  },
];

// ============================================================
// Utilities
// ============================================================
function loadRazorpayScript() {
  return Promise.resolve(false); // no-op — payment flow removed
}

function getEventStartMs() {
  return EVENT_START_UTC;
}

function nextSunday11AMIST() {
  return new Date(getEventStartMs());
}

// ============================================================
// Countdown
// ============================================================
function Countdown() {
  const [now, setNow] = useState(Date.now());
  const target = React.useMemo(() => nextSunday11AMIST(), []);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff / 3_600_000) % 24);
  const mins = Math.floor((diff / 60_000) % 60);
  const secs = Math.floor((diff / 1000) % 60);
  const box = "flex-1 border border-black/10 bg-white text-center py-3 md:py-4 min-w-0";
  return (
    <div className="w-full max-w-xl mx-auto md:mx-0" data-testid="webinar-countdown">
      <p className="webinar-mono text-[10px] md:text-[11px] text-black/60 mb-2 text-center md:text-left">// Webinar starts in — Sat, 19 Sep · 11 AM IST</p>
      <div className="flex gap-2 md:gap-3">
        {[
          { l: "Days", v: days },
          { l: "Hrs", v: hours },
          { l: "Min", v: mins },
          { l: "Sec", v: secs },
        ].map((u) => (
          <div key={u.l} className={box}>
            <div className="text-2xl md:text-4xl font-bold tracking-tight" style={{ color: RED, fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
              {String(u.v).padStart(2, "0")}
            </div>
            <div className="webinar-mono text-[9px] md:text-[10px] text-black/50 mt-0.5">{u.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Registration modal — free signup (name + phone + email)
// ============================================================
const WHATSAPP_COMMUNITY_URL = "https://chat.whatsapp.com/Fx7fJiBE3slDzHhCLBmfme";

function RegisterModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [waUrl, setWaUrl] = useState(WHATSAPP_COMMUNITY_URL);

  React.useEffect(() => {
    if (!open) {
      setName(""); setPhone(""); setEmail(""); setErr(""); setSubmitting(false);
      setDone(false); setWaUrl(WHATSAPP_COMMUNITY_URL);
    }
  }, [open]);

  if (!open) return null;

  const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  const isPhone = (v) => /^\+?[\d\s\-()]{7,}$/.test(v.trim());

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim()) return setErr("Please enter your full name.");
    if (!isPhone(phone)) return setErr("Please enter a valid phone number.");
    if (!isEmail(email)) return setErr("Please enter a valid email address.");
    setSubmitting(true);
    try {
      try {
        if (window.fbq) window.fbq("track", "CompleteRegistration", { content_name: "Webinar" });
        if (window.gtag) window.gtag("event", "generate_lead", { content_name: "Webinar" });
      } catch (e2) { /* swallow */ }
      await axios.post(`${API}/webinar/register`, {
        name: name.trim(), phone: phone.trim(), email: email.trim(),
      });
      // Always use the frontend constant — backend env may lag behind on deployed envs
      setWaUrl(WHATSAPP_COMMUNITY_URL);
      setSubmitting(false);
      setDone(true);
    } catch (e2) {
      setSubmitting(false);
      setErr(e2?.response?.data?.detail || "Could not register right now. Please try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-5"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="webinar-register-modal"
    >
      {done ? (
        <div
          className="bg-white max-w-md w-full p-6 md:p-8 text-center wb-fade-in"
          style={{ borderRadius: "3px" }}
          data-testid="webinar-register-success"
        >
          <div className="flex justify-end">
            <button type="button" onClick={onClose} className="p-1 text-black/50 hover:text-black" aria-label="Close" data-testid="webinar-success-close">
              <XIcon size={20} />
            </button>
          </div>
          <div
            className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-full"
            style={{ border: `3px solid ${RED}` }}
          >
            <Check size={30} strokeWidth={3} style={{ color: RED }} />
          </div>
          <h3 className="text-2xl md:text-3xl font-bold leading-tight text-black" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
            Seat Reserved!
          </h3>
          <p className="webinar-mono mt-3 text-[11px] text-black/60">{EVENT_LABEL_LONG}</p>
          <p className="text-sm text-black/60 mt-4 leading-relaxed">
            Thank you for registering! Click the button below to join our WhatsApp community — that's where all the session details will be shared.
          </p>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 font-semibold text-white px-5 py-3.5 transition-transform hover:-translate-y-[1px] active:translate-y-0"
            style={{ background: RED, borderRadius: "3px", fontFamily: "'Space Grotesk', system-ui, sans-serif", textDecoration: "none" }}
            data-testid="webinar-join-whatsapp"
          >
            Join WhatsApp Community Now <ArrowRight size={18} />
          </a>
          <p className="text-[11px] text-black/50 mt-4">
            Free forever · Join link, reminders & bonuses shared inside
          </p>
        </div>
      ) : (
      <form
        onSubmit={submit}
        className="bg-white max-w-md w-full p-6 md:p-8"
        style={{ borderRadius: "3px" }}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="webinar-eyebrow">Register — free</p>
            <h3 className="mt-1 text-2xl md:text-3xl font-bold leading-tight text-black" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
              Save your seat.
            </h3>
            <p className="text-sm text-black/60 mt-1">{EVENT_LABEL_LONG}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-black/50 hover:text-black" aria-label="Close" data-testid="webinar-register-close">
            <XIcon size={20} />
          </button>
        </div>

        <label className="block text-xs text-black/60 mt-4 mb-1">Full name</label>
        <input
          type="text" required autoFocus value={name} onChange={(e) => setName(e.target.value)}
          className="w-full border border-black/20 px-3 py-2.5 text-black outline-none focus:border-black"
          style={{ borderRadius: "3px" }}
          data-testid="webinar-register-name"
        />

        <label className="block text-xs text-black/60 mt-4 mb-1">Phone number</label>
        <input
          type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)}
          placeholder="+91 98765 43210"
          className="w-full border border-black/20 px-3 py-2.5 text-black outline-none focus:border-black"
          style={{ borderRadius: "3px" }}
          data-testid="webinar-register-phone"
        />

        <label className="block text-xs text-black/60 mt-4 mb-1">Email address</label>
        <input
          type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourbrand.com"
          className="w-full border border-black/20 px-3 py-2.5 text-black outline-none focus:border-black"
          style={{ borderRadius: "3px" }}
          data-testid="webinar-register-email"
        />

        {err && <p className="mt-3 text-xs text-red-600" data-testid="webinar-register-error">{err}</p>}

        <button
          type="submit" disabled={submitting}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 font-semibold text-white px-5 py-3 disabled:opacity-60"
          style={{ background: RED, borderRadius: "3px", fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
          data-testid="webinar-register-submit"
        >
          {submitting ? (<><Loader2 size={16} className="animate-spin" /> Registering…</>) : "Register Now"}
        </button>

        <p className="text-[11px] text-black/50 mt-4 text-center">
          We'll add you to our WhatsApp community after signup. No spam — unsubscribe anytime.
        </p>
      </form>
      )}
    </div>
  );
}

// ============================================================
// Shared CTA button (module-scope)
// ============================================================
function CTAButton({ children, testId, big, onPay, paying }) {
  return (
    <button
      type="button"
      onClick={onPay}
      disabled={paying}
      className={`inline-flex items-center justify-center gap-2 font-semibold text-white transition-transform hover:-translate-y-[1px] active:translate-y-0 disabled:opacity-70 disabled:cursor-wait ${big ? "text-base md:text-lg px-8 py-4 md:px-10 md:py-5" : "text-sm px-5 py-2.5"}`}
      style={{ background: RED, borderRadius: "3px", fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

// ============================================================
// FAQ item
// ============================================================
function FAQItem({ i, faq, open, onToggle }) {
  return (
    <div className="border-t border-black/10" data-testid={`webinar-faq-${i}`}>
      <button
        onClick={onToggle}
        className="w-full text-left py-5 flex items-center justify-between gap-4 hover:opacity-80 transition"
      >
        <span className="text-base md:text-lg font-semibold text-black" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
          {faq.q}
        </span>
        <ChevronDown size={18} className={`text-black/50 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="text-[15px] text-black/70 leading-relaxed pb-5 pr-6">{faq.a}</p>}
    </div>
  );
}

// ============================================================
// Main page
// ============================================================
export default function WebinarLandingPage() {
  const [showRegister, setShowRegister] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    document.title = "Webinar · Digital Sales Engine — Saurabh Khaspuri · BrandChilly";
    try {
      if (window.fbq) window.fbq("track", "ViewContent", { content_name: "WebinarLanding", content_category: "Webinar" });
      if (window.gtag) window.gtag("event", "view_content", { content_name: "WebinarLanding" });
    } catch (e) { /* swallow */ }
  }, []);

  const openRegister = () => {
    try {
      if (window.fbq) window.fbq("track", "InitiateCheckout", { content_name: "Webinar" });
    } catch (e) { /* swallow */ }
    setShowRegister(true);
  };
  const paying = false; // legacy prop for CTAButton; disabled state no longer used

  return (
    <div className="min-h-screen bg-white text-black webinar-scope" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      <style>{`
        .webinar-scope { --wb-red: ${RED}; }
        .webinar-scope h1, .webinar-scope h2, .webinar-scope h3 { font-family: 'Space Grotesk', system-ui, sans-serif; letter-spacing: -0.01em; }
        .webinar-mono { font-family: 'IBM Plex Mono', ui-monospace, monospace; text-transform: uppercase; letter-spacing: 0.16em; }
        .webinar-eyebrow { font-family: 'IBM Plex Mono', ui-monospace, monospace; text-transform: uppercase; letter-spacing: 0.24em; color: ${RED}; font-size: 11px; }
        @keyframes wbGrowBar { from { width: 0; } to { width: 92%; } }
        .wb-progress { height: 6px; background: ${RED}; animation: wbGrowBar 1.4s ease-out both; }
        @keyframes wbFadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
        .wb-fade-in { animation: wbFadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both; }
        @media (prefers-reduced-motion: reduce) { .wb-fade-in { animation: none; } }
      `}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap" />

      {/* ============================================================
          Top bar
      ============================================================ */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-black/10">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-3.5 flex items-center justify-between">
          <a href="/" className="text-lg md:text-xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }} data-testid="webinar-logo">
            BRAND<span style={{ color: RED }}>CHILLY</span>
          </a>
          <CTAButton testId="webinar-topbar-cta" onPay={openRegister} paying={paying}>
            Register Free
          </CTAButton>
        </div>
      </header>

      {/* ============================================================
          Hero
      ============================================================ */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-12 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-7">
            <p className="webinar-eyebrow">Live Webinar · Sat, 19 Sep · 11:00 AM–2:00 PM IST</p>
            <h1 className="mt-4 md:mt-5 text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.06] font-bold text-black" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
              Join to learn how to build a{" "}
              <span style={{ color: RED }}>Digital Sales Engine</span>{" "}
              that brings you predictable clients — without relying heavily on tech.
            </h1>
            <p className="mt-5 md:mt-6 text-lg text-black/70 leading-relaxed max-w-2xl">
              Join <span className="font-semibold text-black">Saurabh Khaspuri — Ex-CMO of a ₹100 Cr+ brand</span> — live, and learn the exact system behind <span className="font-semibold text-black">₹200 Cr in client sales</span>. Stop chasing leads and start closing them, on repeat. <span className="font-semibold text-black">Free — one-time live session.</span>
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {["Sat, 19 Sep 2026", "11:00 AM – 2:00 PM IST", "Live + Q&A", "Free registration"].map((c) => (
                <span key={c} className="inline-flex items-center gap-2 border border-black/15 bg-white text-xs md:text-sm px-3 py-1.5" style={{ borderRadius: "3px" }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: RED }} />
                  {c}
                </span>
              ))}
            </div>

            <div className="mt-7 md:mt-8">
              <CTAButton testId="webinar-hero-cta" big onPay={openRegister} paying={paying}>
                Register Free <ArrowRight size={18} />
              </CTAButton>
              <p className="mt-3 text-xs md:text-sm text-black/60 italic">Limited seats to keep the session interactive. Zero payment — quick 30-second signup.</p>
            </div>

            <div className="mt-8 md:mt-10">
              <Countdown />
            </div>
          </div>

          {/* Stat panel */}
          <div className="lg:col-span-5">
            <div className="border border-black bg-white p-6 md:p-7" style={{ borderRadius: "3px" }} data-testid="webinar-stat-panel">
              <p className="webinar-mono text-[10px] text-black/50 mb-4">// Live System Status</p>
              <div className="border-b border-black/10 pb-4">
                <div className="text-4xl md:text-5xl font-bold leading-none tracking-tight" style={{ color: RED, fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                  ₹200Cr+
                </div>
                <div className="webinar-mono text-[10px] text-black/60 mt-2">Client sales generated</div>
                <div className="mt-3 h-1.5 bg-black/5 relative overflow-hidden" style={{ borderRadius: "2px" }}>
                  <div className="wb-progress" style={{ borderRadius: "2px" }} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-5">
                {[
                  { v: "₹20Cr", l: "Ad spend deployed" },
                  { v: "5 Yrs", l: "System in the field" },
                  { v: "8", l: "Pillars of Sales" },
                  { v: "3 Hrs", l: "Full breakdown" },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="text-xl md:text-2xl font-bold tracking-tight" style={{ color: RED, fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>{s.v}</div>
                    <div className="webinar-mono text-[9px] md:text-[10px] text-black/50 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          The Problem
      ============================================================ */}
      <section className="bg-[#F5F5F5] border-y border-black/5" data-testid="webinar-problem-section">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <p className="webinar-eyebrow">Why Most Founders Stay Stuck</p>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold leading-[1.1] max-w-3xl">
            You're not short on effort. You're short on a <span style={{ color: RED }}>system</span>.
          </h2>
          <p className="mt-4 text-base md:text-lg text-black/70 leading-relaxed max-w-3xl">
            Most founders generate 200–600 leads a month and still close only 2–3 clients. This webinar exists to fix exactly that — so your effort finally turns into income.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PROBLEMS.map((p, i) => (
              <div key={p.t} className="bg-white border border-black/10 p-5" style={{ borderRadius: "3px" }}>
                <div className="webinar-mono text-lg font-bold" style={{ color: RED }}>{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-3 font-bold text-lg text-black leading-tight" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>{p.t}</div>
                <div className="mt-2 text-sm text-black/70 leading-relaxed">{p.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          What You'll Walk Away With — outcome-led
      ============================================================ */}
      <section className="bg-white" data-testid="webinar-outcomes-section">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <p className="webinar-eyebrow">What You'll Walk Away With</p>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold leading-[1.1] max-w-4xl">
            In 3 hours, you'll learn how to turn your expertise into <span style={{ color: RED }}>predictable income</span>.
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {OUTCOMES.map((o, i) => (
              <div key={o.t} className="border border-black/10 p-6 flex gap-4" style={{ borderRadius: "3px" }} data-testid={`webinar-outcome-${i + 1}`}>
                <div className="webinar-mono text-2xl font-bold shrink-0 w-10" style={{ color: RED, fontFamily: "'Space Grotesk', monospace" }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <div className="font-bold text-lg md:text-xl text-black leading-tight" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                    {o.t}
                  </div>
                  <div className="mt-2 text-[15px] text-black/70 leading-relaxed">{o.d}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 border-l-4 pl-5 md:pl-6 max-w-3xl" style={{ borderLeftColor: RED }}>
            <p className="text-base md:text-lg text-black/85 leading-relaxed italic">
              By the end of this webinar, you'll have a clear, working plan to go from unpredictable leads to a steady stream of paying clients — the same plan that's helped founders scale from 0 to lakhs in monthly revenue.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================
          Who this is for
      ============================================================ */}
      <section className="bg-[#F5F5F5] border-y border-black/5">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <p className="webinar-eyebrow">Is This Webinar For You</p>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold leading-[1.1] max-w-3xl">Built for founders selling their own expertise.</h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {WHO_FOR.map((w, i) => (
              <div
                key={i}
                className={`border p-5 flex items-start gap-4 ${w.yes ? "border-black/15 bg-white" : "border-red-500/40 bg-red-50/50"}`}
                style={{ borderRadius: "3px" }}
              >
                <div
                  className="shrink-0 w-9 h-9 flex items-center justify-center text-white"
                  style={{ background: w.yes ? "#0A0A0A" : RED, borderRadius: "3px" }}
                >
                  {w.yes ? <Check size={18} /> : <XIcon size={18} />}
                </div>
                <p className="text-[15px] leading-relaxed text-black">{w.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          Founder / Authority — About Saurabh (photo + navy panel + fade-in)
      ============================================================ */}
      <section className="bg-white" data-testid="webinar-authority-section">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <p className="webinar-eyebrow wb-fade-in">Learn Directly From Someone Who's Done It</p>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold leading-[1.1] max-w-3xl wb-fade-in" style={{ animationDelay: "80ms" }}>
            Meet <span style={{ color: RED }}>Saurabh Khaspuri</span>.
          </h2>

          <div className="mt-10 md:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-stretch">
            {/* Portrait card — dark navy backdrop */}
            <div className="lg:col-span-5 wb-fade-in" style={{ animationDelay: "160ms" }}>
              <div
                className="relative overflow-hidden h-full"
                style={{
                  background: "linear-gradient(180deg, #0B1220 0%, #050912 100%)",
                  borderRadius: "3px",
                  boxShadow: "0 24px 60px -20px rgba(11,18,32,0.55)",
                }}
                data-testid="webinar-founder-portrait"
              >
                <img
                  src="/assets/saurabh.jpg"
                  alt="Saurabh Khaspuri — Founder, BrandChilly"
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                  style={{ aspectRatio: "3 / 4", maxHeight: "620px" }}
                />
                {/* Subtle red corner accent + mono tag */}
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: RED }} />
                  <span
                    className="webinar-mono text-[9px] tracking-[0.24em] text-white/70"
                    style={{ textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}
                  >
                    Founder · BrandChilly
                  </span>
                </div>
                {/* Bottom gradient overlay + red bottom line */}
                <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: RED }} />
              </div>
            </div>

            {/* Bio + stats + quote */}
            <div className="lg:col-span-7 flex flex-col gap-5">
              <div
                className="wb-fade-in p-6 md:p-8"
                style={{
                  animationDelay: "220ms",
                  background: "linear-gradient(180deg, #0B1220 0%, #0A1120 100%)",
                  color: "#F5F7FA",
                  borderRadius: "3px",
                }}
                data-testid="webinar-founder-bio"
              >
                <div className="text-2xl md:text-3xl font-bold leading-tight" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                  Saurabh Khaspuri
                </div>
                <div className="text-sm mt-1 text-white/70">
                  Founder, BrandChilly <span className="mx-1 text-white/40">·</span>{" "}
                  <span className="font-semibold" style={{ color: "#FF6B70" }}>Ex-CMO of a ₹100 Cr+ Brand</span>
                </div>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { v: "15+", l: "Yrs in brand & marketing" },
                    { v: "3.54M", l: "YouTube subs built" },
                    { v: "898K", l: "Instagram followers built" },
                  ].map((r) => (
                    <div key={r.l} className="border-l-2 pl-3 py-1" style={{ borderLeftColor: RED }}>
                      <div className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "#FFFFFF", fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>{r.v}</div>
                      <div className="webinar-mono text-[9px] text-white/50 mt-1 leading-snug">{r.l}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className="wb-fade-in bg-white border border-black/10 p-6 md:p-8 border-l-4"
                style={{ animationDelay: "300ms", borderLeftColor: RED, borderLeftWidth: 4, borderRadius: "3px" }}
                data-testid="webinar-founder-quote"
              >
                <p className="text-base md:text-lg leading-relaxed text-black/90">
                  Before building BrandChilly, Saurabh served as CMO of a <span className="font-bold" style={{ color: RED }}>₹100 crore+ brand</span> — and in the last 5 years has helped multiple coaches generate over <span className="font-bold" style={{ color: RED }}>₹200 crore in sales</span> on just <span className="font-bold" style={{ color: RED }}>₹20 crore in ad spend</span>.
                </p>
                <p className="mt-3 text-base md:text-lg leading-relaxed text-black/75">
                  In this webinar, he teaches you the exact same system he's used at both ends: inside a large brand, and building founder-led businesses from zero.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          Clients — static grid, full color, transparent tiles
      ============================================================ */}
      <section className="bg-[#F5F5F5] border-y border-black/5" data-testid="webinar-clients-section">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <div className="mb-8 md:mb-10">
            <p className="webinar-eyebrow">Trusted by Industry Leaders</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-bold leading-[1.1]">
              Clients we've <span style={{ color: RED }}>worked with</span>.
            </h2>
          </div>

          <div
            className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4 md:gap-6"
            data-testid="webinar-clients-grid"
          >
            {CLIENTS.map((c) => (
              <div
                key={c.name}
                className="h-20 md:h-24 flex items-center justify-center px-2 md:px-3"
                data-testid={`webinar-client-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {c.src ? (
                  <img
                    src={c.src}
                    alt={c.name}
                    loading="lazy"
                    draggable={false}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <div className="text-center">
                    <div
                      className="text-sm md:text-base font-bold text-black leading-tight"
                      style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                    >
                      {c.name}
                    </div>
                    <div className="webinar-mono text-[8px] md:text-[9px] text-black/40 mt-1">Client</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          Testimonials
      ============================================================ */}
      <section className="bg-[#F5F5F5] border-y border-black/5" data-testid="webinar-testimonials-section">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <p className="webinar-eyebrow">Results From Founders Like You</p>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold leading-[1.1] max-w-3xl">
            Real people. Real systems. <span style={{ color: RED }}>Real results.</span>
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white border border-black/10 p-6 md:p-7 relative" style={{ borderRadius: "3px" }} data-testid={`webinar-testimonial-${i + 1}`}>
                <Quote size={22} className="opacity-30 mb-3" style={{ color: RED }} />
                <p className="text-[15px] md:text-base text-black/85 leading-relaxed italic">"{t.q}"</p>
                <div className="mt-5 pt-4 border-t border-black/10 flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center text-white font-bold text-xs" style={{ background: "#0A0A0A", borderRadius: "3px", fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                    {t.who.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
                  </div>
                  <div className="text-sm text-black/70">— <span className="font-semibold text-black">{t.who}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          Register
      ============================================================ */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <div className="border-2 p-7 md:p-10" style={{ borderColor: RED, borderRadius: "3px" }} data-testid="webinar-register-card">
            <p className="webinar-eyebrow">Reserve Your Seat</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-bold leading-[1.1]">
              Your seat for the <span style={{ color: RED }}>19 September</span> webinar is <span style={{ color: RED }}>free</span>.
            </h2>
            <p className="mt-4 text-base md:text-lg text-black/70 leading-relaxed">
              That's 3 hours of live teaching, real frameworks, and a live Q&A with Saurabh — everything you need to leave with a working plan, not just notes.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              {[
                { l: "Day", v: "Saturday" },
                { l: "Date", v: "19 September 2026" },
                { l: "Time", v: "11:00 AM – 2:00 PM IST" },
                { l: "Format", v: "Live online + Q&A" },
                { l: "Seat", v: "Free", big: true },
              ].map((r) => (
                <div key={r.l} className="flex items-baseline justify-between border-b border-black/10 pb-2">
                  <div className="webinar-mono text-[10px] text-black/50">{r.l}</div>
                  <div className={`font-semibold ${r.big ? "text-2xl md:text-3xl" : "text-base"}`} style={r.big ? { color: RED, fontFamily: "'Space Grotesk', system-ui, sans-serif" } : {}}>{r.v}</div>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <CTAButton testId="webinar-register-cta" big onPay={openRegister} paying={paying}>
                Register Free <ArrowRight size={18} />
              </CTAButton>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          FAQ
      ============================================================ */}
      <section className="bg-[#F5F5F5] border-y border-black/5">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <p className="webinar-eyebrow">Questions</p>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold leading-[1.1]">Before you book</h2>
          <div className="mt-8">
            {FAQS.map((f, i) => (
              <FAQItem key={f.q} i={i} faq={f} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
            <div className="border-t border-black/10" />
          </div>
        </div>
      </section>

      {/* ============================================================
          Final CTA
      ============================================================ */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20 text-center">
          <p className="webinar-eyebrow">Sat, 19 Sep · 11 AM–2 PM · Free</p>
          <h2 className="mt-3 text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.08]">
            Build the system once.{" "}
            <span style={{ color: RED }}>Let it bring you clients every week after.</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-black/70 leading-relaxed max-w-2xl mx-auto">
            Seats are capped to keep the webinar interactive — register now, it's free.
          </p>
          <div className="mt-8">
            <CTAButton testId="webinar-final-cta" big onPay={openRegister} paying={paying}>
              Register Free <ArrowRight size={18} />
            </CTAButton>
          </div>
        </div>
      </section>

      {/* ============================================================
          Footer
      ============================================================ */}
      <footer className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
              BRAND<span style={{ color: RED }}>CHILLY</span>
              <span className="webinar-mono text-[10px] text-white/50 ml-3">— Personal Branding & Business Growth Agency</span>
            </div>
          </div>
          <div className="text-sm text-white/70">
            <a href="mailto:brandchilly@gmail.com" className="hover:text-white">brandchilly@gmail.com</a>
            <span className="mx-2 text-white/30">·</span>
            <a href="tel:+918979106414" className="hover:text-white">+91 89791 06414</a>
          </div>
        </div>
      </footer>

      <RegisterModal open={showRegister} onClose={() => setShowRegister(false)} />
    </div>
  );
}
